import { useCallback, useEffect, useState } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Stack, Alert, CircularProgress, MenuItem,
} from '@mui/material';
import { getAuditLog } from '../../api/saasApi';

// The actions the backend currently records, for a quick filter.
const ACTIONS = [
  '', 'tenant.signup', 'tenant.set_status', 'tenant.rotate_join_code',
  'membership.requested', 'membership.approved', 'membership.rejected',
  'user.invite', 'user.assign_role',
  'role.create', 'role.set_permissions', 'role.delete',
  'impersonation.start', 'platform.set_admin',
  'patient.update', 'patient.delete',
];

/**
 * Entries that store JSON with a `summary` (e.g. patient.delete) show that
 * sentence; the full JSON is in the tooltip. Anything else shows as-is.
 */
const detailText = (metadata) => {
  if (!metadata) return { text: '—', full: '' };
  try {
    const parsed = JSON.parse(metadata);
    if (parsed && typeof parsed.summary === 'string') {
      return { text: parsed.summary, full: JSON.stringify(parsed, null, 2) };
    }
  } catch (e) { /* plain text metadata */ }
  return { text: metadata, full: '' };
};

/** Filterable view of the AuditLog table, which until now had no UI at all. */
const AuditLog = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ action: '', tenantId: '', from: '', to: '' });

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await getAuditLog({
        action: f.action || null,
        tenantId: f.tenantId ? Number(f.tenantId) : null,
        from: f.from || null,
        // Inclusive end date: the API compares CreatedAt < To.
        to: f.to ? new Date(new Date(f.to).getTime() + 86400000).toISOString() : null,
      });
      if (res.data?.succeeded === false) setError(res.data.messages?.[0] || 'Failed to load audit log.');
      else setRows(res.data?.data || []);
    } catch (e) {
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(filters), 350);
    return () => clearTimeout(timer);
  }, [filters, load]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Audit log</Typography>
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
        <TextField select size="small" label="Action" value={filters.action} onChange={set('action')} sx={{ minWidth: 220 }}>
          {ACTIONS.map((a) => <MenuItem key={a || 'all'} value={a}>{a || 'All actions'}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Clinic ID" value={filters.tenantId} onChange={set('tenantId')} sx={{ width: 130 }} />
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={set('from')} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={set('to')} />
      </Stack>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Clinic</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{r.tenantId ?? '—'}</TableCell>
                  <TableCell>{r.actorUserId ?? '—'}</TableCell>
                  <TableCell>{r.action}</TableCell>
                  <TableCell>{[r.targetType, r.targetId].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell title={detailText(r.metadata).full || undefined} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailText(r.metadata).text}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6}>Nothing matches those filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Container>
  );
};

export default AuditLog;
